import { useCallback, useEffect, useState } from 'react'
import { format } from 'date-fns'
import { usePersistentState } from '@/hooks/use-persistent-state'
import { useUser } from '@/contexts/UserContext'
import { useVersion } from '@/hooks/use-version'
import { clearFreshLogin, peekFreshLogin } from '@/lib/loginSignal'
import { isWorkspaceOnlyUser } from '@/lib/workspaceHome'

/**
 * Whether to greet this person with the welcome-back summary.
 *
 * It opens after a real login, and only then:
 *
 *   - a real login, not a reload  the fresh-login marker (lib/loginSignal.ts) is only left by
 *                                 AuthCallback and Login
 *   - once a day, ON PRODUCTION   the local date it was last dismissed is stored per user, so
 *                                 logging out and back in at lunch does not show it again.
 *                                 On local and dev it opens on EVERY login, so it can be tested
 *                                 without clearing storage between attempts.
 *   - never while impersonating   it summarises YOUR work; the endpoint refuses anyway
 *   - never for HR- or Training-only accounts, which have no CRM work to summarise
 *
 *
 * WHICH ENVIRONMENT, AND WHY IT WAITS
 *
 * The environment comes from the server (useVersion's `envName`, from Cloud Run's K_SERVICE),
 * because the same frontend build runs on dev and prod: import.meta.env cannot tell them apart.
 * See use-environment.ts for the full reasoning.
 *
 * The daily limit is lifted ONLY when the server positively says 'local' or 'dev'. Anything else
 * (prod, an unrecognised service, or the request failing) keeps it. Failing towards production
 * behaviour means the worst a broken version request can do on dev is show the summary once a
 * day; failing the other way would show it to real users on every login.
 *
 * So the decision waits for that answer, then is taken ONCE and latched.
 *
 *
 * NOTHING IS RECORDED UNTIL IT IS CLOSED
 *
 * The login marker stays in place and today's date is only stored on close. If anything
 * remounts the app while the dialog is up (it did: AuthCallback used to bounce through /login
 * straight after a successful login in development), the fresh mount reaches the same decision
 * and the dialog is still there. Recording on open meant a remount found "already shown today"
 * and the summary vanished half a second after it appeared. The cost is that reloading with the
 * dialog still open shows it again, which is right: it has not been dismissed.
 *
 * Must only mount once the user has resolved (see StartupDialogs): the stored date is read in a
 * useState initialiser, which runs once, as with use-whats-new.ts.
 */

/** Environments where the summary opens on every login rather than once a day. */
const EVERY_LOGIN_ENVS = ['local', 'dev']

export function localToday(now: Date = new Date()): string {
  return format(now, 'yyyy-MM-dd')
}

export interface WelcomeBack {
  open: boolean
  close: () => void
}

export function useWelcomeBack(): WelcomeBack {
  const { currentUser, userType, isImpersonating } = useUser()
  const userId = currentUser?.id
  const { data: version, isError: versionFailed } = useVersion()

  const storageKey = userId ? `gracklecrm_welcome_back_shown_${userId}` : undefined
  const [lastShown, setLastShown] = usePersistentState<string | null>(storageKey, null)

  // Read without clearing: see above. Cleared on close, or below when it will not be shown.
  const [freshLogin] = useState(() => Boolean(userId) && peekFreshLogin() === userId)
  const [today] = useState(localToday)

  // null until decided. Decided once the environment is known, or the request has given up.
  const [decision, setDecision] = useState<boolean | null>(null)
  const envKnown = Boolean(version) || versionFailed

  useEffect(() => {
    if (decision !== null) return
    if (!freshLogin) {
      setDecision(false)
      return
    }
    if (!envKnown) return

    const oncePerDay = !EVERY_LOGIN_ENVS.includes(version?.envName ?? '')
    const show =
      !isImpersonating &&
      !isWorkspaceOnlyUser(userType ?? currentUser?.user_type) &&
      !(oncePerDay && lastShown === today)

    setDecision(show)
    // A marker that will not lead to a dialog is spent now, so it cannot open one later in this
    // tab. One that will is kept until the dialog is closed (see above).
    if (!show) clearFreshLogin()
  }, [
    decision,
    freshLogin,
    envKnown,
    version?.envName,
    isImpersonating,
    userType,
    currentUser?.user_type,
    lastShown,
    today,
  ])

  const close = useCallback(() => {
    setDecision(false)
    setLastShown(today)
    clearFreshLogin()
  }, [today, setLastShown])

  return { open: decision === true, close }
}
